import { useEffect, useState, useRef } from "react";
import { doc, getDoc, setDoc, collection, query, where, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../lib/firebase";
import { useAuth } from "../../contexts/AuthContext";
import { Radio, Upload, Save, CheckCircle, AlertCircle, Clock } from "lucide-react";
import { Link } from "react-router-dom";

const GENRES = ["House", "Techno", "Hip-Hop", "R&B", "Jazz", "Electronic", "Reggae", "Various", "Mixed", "Other"];
const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function DashboardStation() {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", description: "", genre: "Various", autodjEnabled: false });
  const [logoFile, setLogoFile] = useState(null);
  const [logoURL, setLogoURL] = useState("");
  const [stationId, setStationId] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const logoRef = useRef();

  useEffect(() => { loadStation(); }, [user]);

  const loadStation = async () => {
    if (!user) return;
    try {
      const q = query(collection(db, "stations"), where("creatorId", "==", user.uid));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const stationData = { id: snap.docs[0].id, ...snap.docs[0].data() };
        setStationId(stationData.id);
        setForm({
          name: stationData.name || "",
          description: stationData.description || "",
          genre: stationData.genre || "Various",
          autodjEnabled: stationData.autodjEnabled || false,
        });
        setLogoURL(stationData.logoURL || "");

        // Load schedules
        const schedQ = query(collection(db, "schedules"), where("stationId", "==", stationData.id));
        const schedSnap = await getDocs(schedQ);
        setSchedules(schedSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      let finalLogoURL = logoURL;
      if (logoFile) {
        const storageRef = ref(storage, `users/${user.uid}/station/${Date.now()}_logo`);
        const task = uploadBytesResumable(storageRef, logoFile);
        await new Promise((res, rej) => {
          task.on("state_changed",
            (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
            rej,
            res
          );
        });
        finalLogoURL = await getDownloadURL(task.snapshot.ref);
        setLogoURL(finalLogoURL);
      }

      const slug = form.name.toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "");
      const data = { ...form, slug, logoURL: finalLogoURL, creatorId: user.uid, status: stationId ? undefined : "pending", updatedAt: serverTimestamp() };
      if (!stationId) { data.createdAt = serverTimestamp(); data.showCount = 0; }

      if (stationId) {
        await setDoc(doc(db, "stations", stationId), data, { merge: true });
      } else {
        const newDoc = await addDoc(collection(db, "stations"), { ...data, status: "pending", showCount: 0, createdAt: serverTimestamp() });
        setStationId(newDoc.id);
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { setError(err.message); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-[#B026FF] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="dashboard-station" className="space-y-6 max-w-2xl">
      <h2 className="text-xl font-bold text-white">My Station</h2>
      {stationId && (
        <Link to={`/radio/${stationId}`} className="text-[#B026FF] text-xs hover:underline">View Public Station →</Link>
      )}

      <form onSubmit={handleSave} className="space-y-4">
        {/* Logo */}
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-2xl bg-[#111] border border-white/10 overflow-hidden flex-shrink-0">
            {logoURL ? (
              <img src={logoURL} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><Radio size={24} className="text-zinc-600" /></div>
            )}
          </div>
          <div>
            <button type="button" onClick={() => logoRef.current?.click()}
              className="flex items-center gap-2 text-xs border border-white/10 px-3 py-2 rounded-lg text-zinc-400 hover:border-white/30 transition-colors">
              <Upload size={12} /> Upload Logo
            </button>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && setLogoFile(e.target.files[0])} />
            {logoFile && <p className="text-zinc-600 text-xs mt-1 truncate max-w-[180px]">{logoFile.name}</p>}
          </div>
        </div>

        <input required type="text" placeholder="Station Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#B026FF]/50" />

        <textarea placeholder="Station Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
          className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#B026FF]/50 resize-none" />

        <select value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })}
          className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#B026FF]/50">
          {GENRES.map((g) => <option key={g}>{g}</option>)}
        </select>

        <label className="flex items-center gap-3 cursor-pointer">
          <div className={`relative w-10 h-5 rounded-full transition-colors ${form.autodjEnabled ? "bg-[#B026FF]" : "bg-[#1a1a1a] border border-white/10"}`}>
            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.autodjEnabled ? "translate-x-5" : "translate-x-0.5"}`} />
          </div>
          <input type="checkbox" checked={form.autodjEnabled} onChange={(e) => setForm({ ...form, autodjEnabled: e.target.checked })} className="hidden" />
          <span className="text-zinc-300 text-sm">Enable Auto-DJ (fills empty slots with your approved tracks)</span>
        </label>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl p-3">
            <AlertCircle size={14} className="text-red-400" />
            <p className="text-red-400 text-xs">{error}</p>
          </div>
        )}

        {!stationId && (
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
            <p className="text-blue-400 text-xs">New stations require admin approval before going public.</p>
          </div>
        )}

        <button type="submit" disabled={saving} data-testid="save-station-btn"
          className="flex items-center gap-2 bg-[#B026FF] text-white font-bold px-6 py-2.5 rounded-full hover:bg-[#9b1ee6] transition-colors disabled:opacity-50">
          {saved ? <><CheckCircle size={16} /> Saved!</> : saving ? "Saving..." : <><Save size={16} /> Save Station</>}
        </button>
      </form>

      {/* 7-Day Schedule Preview */}
      {stationId && schedules.length > 0 && (
        <div>
          <h3 className="text-base font-bold text-white mb-3 flex items-center gap-2">
            <Clock size={16} className="text-[#B026FF]" /> Schedule Preview
          </h3>
          <div className="grid grid-cols-7 gap-1">
            {DAYS_SHORT.map((day, i) => {
              const daySchedules = schedules.filter((s) => s.dayOfWeek === i);
              const isToday = i === new Date().getDay();
              return (
                <div key={day} className={`rounded-xl p-2 min-h-[80px] border ${isToday ? "border-[#B026FF]/50 bg-[#B026FF]/5" : "border-white/5 bg-[#111]"}`}>
                  <p className={`text-[10px] font-bold mb-1 ${isToday ? "text-[#B026FF]" : "text-zinc-600"}`}>{day}</p>
                  <div className="space-y-1">
                    {daySchedules.map((s, j) => (
                      <div key={j} className="text-[8px] bg-[#B026FF]/20 text-[#B026FF] px-1 py-0.5 rounded truncate">
                        {s.startTime} {s.showTitle && `- ${s.showTitle}`}
                      </div>
                    ))}
                    {daySchedules.length === 0 && <p className="text-[9px] text-zinc-700">Empty</p>}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-zinc-600 text-xs mt-2">
            <Link to="/dashboard/shows" className="text-[#B026FF] hover:underline">Manage Shows →</Link>
          </p>
        </div>
      )}
    </div>
  );
}
